import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../../ui/dialog';
import { Gauge, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBytes = (n) => {
  const v = Number(n || 0);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtUptime = (sec) => {
  const s = Number(sec || 0);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${s % 60}s`;
  return `${s}s`;
};

const rateColor = (pct) => {
  if (pct >= 70) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (pct >= 40) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-slate-700 bg-slate-50 border-slate-200';
};

/**
 * Dialog-based view of live ETag / 304 poll cache metrics. Only fetches
 * when opened; no background polling. Closes cleanly.
 *
 * Helps admins answer: "Is the ETag layer saving bandwidth?"
 *   70%+ = healthy, 40-70% = warming, <40% = cache churn → investigate.
 */
export const PollCacheMetricsCard = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/metrics/poll-cache-hit-rate`);
      setData(resp.data);
      setError(null);
    } catch (e) {
      if (e?.response?.status !== 403) {
        setError(e?.response?.data?.detail || 'Failed to load metrics');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch once when the dialog opens; do not auto-refresh in the background.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const overall = Number(data?.overall_hit_rate_pct ?? 0);

  return (
    <Card className="border-teal-200" data-testid="poll-cache-metrics-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Gauge className="h-5 w-5 text-teal-600" />
          Poll Cache Hit Rate
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-gray-500 mb-3">
          Live stats on how much bandwidth the ETag / 304 Not Modified layer is
          saving on hot poll endpoints (notifications, staff messages, static
          config). Click the button to view the breakdown.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="border-teal-600 text-teal-700 hover:bg-teal-50"
              data-testid="poll-cache-open-dialog-btn"
            >
              <Gauge className="h-4 w-4 mr-1" />
              View Hit Rate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-teal-600" />
                  Poll Cache Hit Rate
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={load}
                  disabled={loading}
                  data-testid="poll-cache-refresh-btn"
                  className="h-8 mr-6"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </DialogTitle>
              <DialogDescription>
                Aggregate counters since the backend process started. No PHI,
                no per-user data — only paths, counters, and byte totals.
              </DialogDescription>
            </DialogHeader>

            {error && (
              <div className="text-sm text-rose-600" data-testid="poll-cache-error">{error}</div>
            )}

            {loading && !data && (
              <div className="py-6 text-center text-sm text-gray-500">Loading metrics…</div>
            )}

            {data && (
              <>
                {/* Aggregate rollup */}
                <div className={`grid grid-cols-2 md:grid-cols-4 gap-2 p-3 rounded-lg border ${rateColor(overall)}`}>
                  <div>
                    <div className="text-xs opacity-70">Overall</div>
                    <div className="text-2xl font-bold" data-testid="poll-cache-overall-rate">
                      {overall.toFixed(1)}%
                    </div>
                    <div className="text-[10px] opacity-60">{data.total_304}/{data.total_requests} as 304</div>
                  </div>
                  <div>
                    <div className="text-xs opacity-70">Bytes Saved</div>
                    <div className="text-lg font-semibold" data-testid="poll-cache-bytes-saved">
                      {fmtBytes(data.total_bytes_saved_est)}
                    </div>
                    <div className="text-[10px] opacity-60">est. since restart</div>
                  </div>
                  <div>
                    <div className="text-xs opacity-70">Total Polls</div>
                    <div className="text-lg font-semibold">{data.total_requests}</div>
                    <div className="text-[10px] opacity-60">on cached endpoints</div>
                  </div>
                  <div>
                    <div className="text-xs opacity-70">Uptime</div>
                    <div className="text-lg font-semibold">{fmtUptime(data.uptime_sec)}</div>
                    <div className="text-[10px] opacity-60">since backend start</div>
                  </div>
                </div>

                {/* Per-endpoint table */}
                <div className="overflow-x-auto mt-3">
                  <table
                    className="min-w-full text-xs"
                    data-testid="poll-cache-endpoints-table"
                  >
                    <thead className="text-gray-500">
                      <tr>
                        <th className="text-left p-1.5">Endpoint</th>
                        <th className="text-right p-1.5">200</th>
                        <th className="text-right p-1.5">304</th>
                        <th className="text-right p-1.5">Hit Rate</th>
                        <th className="text-right p-1.5">Avg Body</th>
                        <th className="text-right p-1.5">Saved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.endpoints || []).map((ep) => (
                        <tr
                          key={ep.path}
                          className="border-t hover:bg-gray-50"
                          data-testid={`poll-cache-row-${ep.path}`}
                        >
                          <td className="p-1.5 font-mono text-[10px]">{ep.path}</td>
                          <td className="p-1.5 text-right">{ep.hits_200}</td>
                          <td className="p-1.5 text-right">{ep.hits_304}</td>
                          <td className="p-1.5 text-right">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${rateColor(ep.hit_rate)}`}
                            >
                              {ep.hit_rate}%
                            </Badge>
                          </td>
                          <td className="p-1.5 text-right text-gray-500">{fmtBytes(ep.avg_body_bytes)}</td>
                          <td className="p-1.5 text-right text-emerald-700">{fmtBytes(ep.bytes_saved_est)}</td>
                        </tr>
                      ))}
                      {(data.endpoints || []).length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-3 text-center text-gray-500">
                            No cached polls yet — metrics populate as users browse.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <p className="text-[10px] text-gray-400 mt-2">
                  Data is aggregate, in-memory, and resets on backend restart. Click the refresh icon above to re-fetch.
                </p>
              </>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PollCacheMetricsCard;
